import React, { useContext, useEffect, useMemo, useState } from "react";
import { PaymentContext } from "../../context/paymentContext";
import countryList from "react-select-country-list";
import { httpGet, httpPatch, httpPost } from "../../components/helpers/httpMethods";
import UploadImage from '../../components/helpers/uploadImage';
// Same plans, prices and plan names as the subscribe window (copied from SubscribeSheet.jsx at build time)
const GIFT_PLANS = [
  { key: "one-off", label: "Single edition", ngn: "oneOffFee", usd: ["oneOffDollarFee", "oneOffFeeDollarFee"], days: 1 },
  { key: "weekly", label: "Weekly", ngn: "weeklyFee", usd: ["weeklyDollarFee", "weeklyFeeDollarFee"], days: 7 },
  { key: "monthly", label: "Monthly", ngn: "monthlyFee", usd: ["monthlyDollarFee", "monthlyFeeDollarFee"], months: 1, popular: true },
  { key: "quarterly", label: "Every 3 months", ngn: "quarterlyFee", usd: ["quarterlyDollarFee", "quarterlyFeeDollarFee"], months: 3 },
  { key: "biannually", label: "Every 6 months", ngn: "biannuallyFee", usd: ["biannuallyDollarFee", "biannualDollarFee", "biannuallyFeeDollarFee"], months: 6 },
  { key: "annually", label: "Yearly", ngn: "annualFee", usd: ["annualDollarFee", "annualFeeDollarFee"], months: 12 },
];

// Account hub: Account settings and Gifts for others.
// Both use the same server calls as the classic pages (auth/profile_details, auth/edit_profile,
// auth/change_password, bought_by_me, cancel_buy_for_me_subscription) so nothing on the server changes.

const CODES = [["+234", "Nigeria"], ["+233", "Ghana"], ["+254", "Kenya"], ["+27", "South Africa"], ["+44", "United Kingdom"], ["+1", "US / Canada"],
  ["+971", "UAE"], ["+353", "Ireland"], ["+49", "Germany"], ["+33", "France"], ["+61", "Australia"], ["+91", "India"], ["+86", "China"]];
const errText = (r, fallback) => (r && r.er && (r.er.message || (typeof r.er === "string" ? r.er : ""))) || fallback;
const titleCase = (s) => (s && s === s.toUpperCase() ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : s || "");
const day = (d) => {
  const x = d ? new Date(d) : null;
  return x && !Number.isNaN(x.getTime()) ? x.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Africa/Lagos" }) : "";
};
const naira = (v) => `₦${Math.round(Number(v) || 0).toLocaleString("en-NG")}`;
const PLAN = { "one-off": "Single edition", weekly: "Weekly", monthly: "Monthly", quarterly: "Every 3 months", biannually: "Every 6 months", biAnnual: "Every 6 months", annually: "Yearly", annual: "Yearly" };

function Note({ note }) {
  if (!note) return null;
  return <p className={`ac-note ${note.ok ? "ok" : "err"}`} role={note.ok ? "status" : "alert"}>{note.text}</p>;
}

export function Settings() {
  const countries = useMemo(() => countryList().getData().map((c) => c.label), []);
  const [p, setP] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [pwBusy, setPwBusy] = useState(false);
  const [pwNote, setPwNote] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoNote, setPhotoNote] = useState(null);

  const load = () => httpGet("auth/profile_details").then((r) => {
    const d = r && r.data;
    const u = d ? [d.user, d.profile, d.userDetails, d.data, d].find((x) => x && x.email) : null;
    if (!u) { setLoadErr(errText(r, "We couldn’t load your details. Please refresh the page.")); return; }
    setP(u);
    setForm({ firstName: u.firstName || "", lastName: u.lastName || "", code: u.countryCode || "+234", phone: u.phoneNumber || "", country: u.country || "Nigeria" });
  });
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setNote(null); };
  const digits = form ? String(form.phone).replace(/\D/g, "") : "";
  const codes = form && !CODES.some(([c]) => c === form.code) ? [[form.code, "Current"], ...CODES] : CODES;

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return setNote({ ok: false, text: "Please enter your first and last name." });
    if (digits.length < 7) return setNote({ ok: false, text: "Please enter a valid phone number." });
    if (!form.country) return setNote({ ok: false, text: "Please choose your country." });
    const data = {
      bio: p.bio, country: form.country, email: p.email, fbHandle: p.fbHandle, firstName: form.firstName.trim(), instagramHandle: p.instagramHandle,
      countryCode: form.code, phoneNumber: digits.slice(-10), jobPosition: p.jobPosition, lastName: form.lastName.trim(), twitterHandle: p.twitterHandle,
      username: p.username, youtubeHandle: p.youtubeHandle,
    };
    setSaving(true);
    const r = await httpPatch("auth/edit_profile", data);
    setSaving(false);
    if (!r || r.er) return setNote({ ok: false, text: errText(r, "We couldn’t save your details. Please try again.") });
    setNote({ ok: true, text: "Your details are saved." });
    load();
  };

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    setPhotoNote(null);
    if (!f) return;
    if (!/^image\//.test(f.type)) return setPhotoNote({ ok: false, text: "Please choose a picture (JPG or PNG)." });
    if (f.size > 10 * 1024 * 1024) return setPhotoNote({ ok: false, text: "That picture is over 10 MB. Please choose a smaller one." });
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const savePhoto = async () => {
    if (!file || !UploadImage) return;
    setPhotoBusy(true);
    try {
      const im = await UploadImage(file);
      if (!im || !im.imagePath) throw new Error("upload");
      // photo saved with your details: the server checks the full profile on every update
      const r = await httpPatch("auth/edit_profile", {
        avatar: im.imagePath, bio: p.bio, country: form.country || p.country, email: p.email, fbHandle: p.fbHandle,
        firstName: (form.firstName || p.firstName || "").trim(), lastName: (form.lastName || p.lastName || "").trim(),
        instagramHandle: p.instagramHandle, countryCode: form.code || p.countryCode, phoneNumber: String(form.phone || p.phoneNumber || "").replace(/\D/g, "").slice(-10),
        jobPosition: p.jobPosition, twitterHandle: p.twitterHandle, username: p.username, youtubeHandle: p.youtubeHandle,
      });
      if (!r || r.er) throw new Error(errText(r, ""));
      setPhotoNote({ ok: true, text: "Your photo is updated. Refreshing…" });
      setTimeout(() => window.location.reload(), 900);
      setFile(null);
      load();
    } catch (e) {
      setPhotoNote({ ok: false, text: (e && e.message && e.message !== "upload" && e.message) || "We couldn’t upload that picture. Please try again." });
    }
    setPhotoBusy(false);
  };

  const strength = pw.next.length >= 12 && /\d/.test(pw.next) && /[^A-Za-z0-9]/.test(pw.next) ? ["Strong", "ok"] : pw.next.length >= 8 ? ["Good", "info"] : pw.next ? ["Too short", "warn"] : null;
  const changePassword = async () => {
    if (!pw.current) return setPwNote({ ok: false, text: "Please enter your current password." });
    if (pw.next.length < 8) return setPwNote({ ok: false, text: "Your new password needs at least 8 characters." });
    if (pw.next !== pw.confirm) return setPwNote({ ok: false, text: "The new passwords don’t match." });
    setPwBusy(true);
    const r = await httpPatch("auth/change_password", { newPassword: pw.next, currentPassword: pw.current });
    setPwBusy(false);
    if (!r || r.er) return setPwNote({ ok: false, text: errText(r, "We couldn’t change your password. Please check your current password.") });
    setPw({ current: "", next: "", confirm: "" });
    setPwNote({ ok: true, text: "Your password is changed." });
  };

  if (loadErr) return <div className="ac-card"><p className="ac-muted">{loadErr}</p></div>;
  if (!form) return <p className="ac-muted">Loading your details…</p>;
  const avatar = preview || p.avatar;
  return (
    <>

      <section className="ac-card ac-form">
        <h2 className="ac-form-title">Photo</h2>
        <div className="ac-photo">
          <span className="ac-avatar big">{avatar ? <img src={avatar} alt="" /> : (form.firstName[0] || "")}</span>
          <div className="ac-photo-actions">
            {UploadImage ? (
              <>
                <label className="ac-btn2 ac-file">Choose a picture<input type="file" accept="image/*" onChange={pick} /></label>
                {file ? <button type="button" className="ac-btn" onClick={savePhoto} disabled={photoBusy}>{photoBusy ? "Uploading…" : "Save photo"}</button> : null}
                <small>JPG or PNG, up to 10 MB.</small>
              </>
            ) : <small>Photo changes are available on the classic profile page.</small>}
            <Note note={photoNote} />
          </div>
        </div>
      </section>

      <section className="ac-card ac-form">
        <h2 className="ac-form-title">Your details</h2>
        <div className="ac-grid2">
          <label className="ac-field"><span>First name</span><input className="ac-input" value={form.firstName} onChange={set("firstName")} autoComplete="given-name" /></label>
          <label className="ac-field"><span>Last name</span><input className="ac-input" value={form.lastName} onChange={set("lastName")} autoComplete="family-name" /></label>
          <label className="ac-field"><span>Email</span><input className="ac-input" value={p.email} readOnly aria-readonly="true" /><small>To change your email, contact support.</small></label>
          <div className="ac-field"><span>Phone number</span>
            <div className="ac-phone">
              <select className="ac-input" value={form.code} onChange={set("code")} aria-label="Country code">{codes.map(([c, n]) => <option key={c} value={c}>{c} {n}</option>)}</select>
              <input className="ac-input" value={form.phone} onChange={set("phone")} inputMode="tel" autoComplete="tel-national" aria-label="Phone number" placeholder="901 234 5678" />
            </div>
          </div>
          <label className="ac-field"><span>Country</span>
            <select className="ac-input" value={form.country} onChange={set("country")}>
              {countries.includes(form.country) ? null : <option value={form.country}>{form.country || "Choose…"}</option>}
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>
        <div className="ac-form-foot"><button type="button" className="ac-btn" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save changes"}</button><Note note={note} /></div>
      </section>

      <section className="ac-card ac-form">
        <h2 className="ac-form-title">Password</h2>
        <div className="ac-grid2">
          <label className="ac-field"><span>Current password</span><input className="ac-input" type={showPw ? "text" : "password"} value={pw.current} onChange={(e) => { setPw({ ...pw, current: e.target.value }); setPwNote(null); }} autoComplete="current-password" /></label>
          <span />
          <label className="ac-field"><span>New password</span><input className="ac-input" type={showPw ? "text" : "password"} value={pw.next} onChange={(e) => { setPw({ ...pw, next: e.target.value }); setPwNote(null); }} autoComplete="new-password" />
            {strength ? <small><span className={`ac-pill ${strength[1]}`}>{strength[0]}</span> At least 8 characters; a number and a symbol make it stronger.</small> : <small>At least 8 characters.</small>}</label>
          <label className="ac-field"><span>Confirm new password</span><input className="ac-input" type={showPw ? "text" : "password"} value={pw.confirm} onChange={(e) => { setPw({ ...pw, confirm: e.target.value }); setPwNote(null); }} autoComplete="new-password" /></label>
        </div>
        <div className="ac-form-foot">
          <button type="button" className="ac-btn" onClick={changePassword} disabled={pwBusy}>{pwBusy ? "Changing…" : "Change password"}</button>
          <label className="ac-check"><input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} /> Show passwords</label>
          <Note note={pwNote} />
        </div>
      </section>

    </>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TYPOS = { "gmial.com": "gmail.com", "gamil.com": "gmail.com", "gmail.co": "gmail.com", "gmai.com": "gmail.com", "yaho.com": "yahoo.com", "yahoo.co": "yahoo.com", "hotmial.com": "hotmail.com", "outlok.com": "outlook.com" };

function GiftForm({ onClose, onStarted }) {
  const { setgetPublicationDetails, sendPaymentDetailsToBackend } = useContext(PaymentContext);
  const [pubs, setPubs] = useState(null);
  const [q, setQ] = useState("");
  const [pub, setPub] = useState(null);
  const [plan, setPlan] = useState("");
  const [to, setTo] = useState({ firstName: "", lastName: "", email: "" });
  const [err, setErr] = useState("");
  useEffect(() => {
    setgetPublicationDetails("subAccount");
    httpGet("me/gift_papers").then((r) => {
      if (r && r.data && Array.isArray(r.data.publications)) return setPubs(r.data.publications);
      httpGet("publications").then((r2) => setPubs((r2 && r2.data && r2.data.publications) || []));
    });
  }, []);
  const plansFor = (p) => (p ? GIFT_PLANS.map((x) => ({ ...x, price: Number(p[x.ngn]) || 0 })).filter((x) => x.price > 0 && x.key !== "one-off") : []);
  const list = useMemo(() => {
    // Subscriptions only (not one-off issues or books), one entry per name, newest first
    const seen = new Set();
    const all = (pubs || []).filter((p) => plansFor(p).some((x) => x.key !== "one-off")).filter((p) => { const k = String(p.name || "").trim().toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
    const t = q.trim().toLowerCase();
    return (t ? all.filter((p) => String(p.name || "").toLowerCase().includes(t)) : all).slice(0, 24);
  }, [pubs, q]);
  const plans = plansFor(pub);
  const chosen = plans.find((x) => x.key === plan);
  const domain = to.email.split("@")[1] || "";
  const typo = TYPOS[domain.toLowerCase()];
  const choosePub = (p) => { setPub(p); const ps = plansFor(p); setPlan((ps.find((x) => x.key === "monthly") || ps[0] || {}).key || ""); setErr(""); };
  const pay = () => {
    if (!pub || !chosen) return setErr("Please choose a paper and a plan.");
    if (!to.firstName.trim() || !to.lastName.trim()) return setErr("Please enter their first and last name.");
    if (!EMAIL_RE.test(to.email.trim())) return setErr("Please enter a valid email address for them.");
    setErr("");
    setgetPublicationDetails("subAccount");
    sendPaymentDetailsToBackend(chosen.price, chosen.key, "NGN", pub.id, pub.isPaid, to.firstName.trim(), to.lastName.trim(), to.email.trim());
    onStarted(`${titleCase(pub.name)} · ${chosen.label} for ${to.firstName.trim()} ${to.lastName.trim()}`);
  };
  return (
    <section className="ac-card ac-form ac-giftform" aria-label="Give a gift">
      <div className="ac-head"><h2 className="ac-form-title">Give a gift</h2><button type="button" className="ac-link" onClick={onClose}>Cancel</button></div>

      <div className="ac-step"><span className="ac-step-n">1</span><strong>Choose the paper</strong></div>
      {pub ? (
        <div className="ac-picked">
          <span className="ac-cover">{pub.latestCover || pub.image || pub.logo ? <img src={pub.latestCover || pub.image || pub.logo} alt="" /> : null}</span>
          <strong>{titleCase(pub.name)}</strong>
          <button type="button" className="ac-link" onClick={() => { setPub(null); setPlan(""); }}>Change</button>
        </div>
      ) : (
        <>
          <input className="ac-input" type="search" placeholder="Search papers and magazines" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search papers" autoFocus />
          {pubs === null ? <p className="ac-muted">Loading papers…</p> : !list.length ? <p className="ac-muted">No paper matches “{q}”.</p> : (
            <div className="ac-pubgrid">
              {list.map((p) => (
                <button type="button" key={p.id} className="ac-pub-item" onClick={() => choosePub(p)}>
                  <span className="ac-cover">{p.latestCover || p.image || p.logo ? <img src={p.latestCover || p.image || p.logo} alt="" loading="lazy" /> : null}</span>
                  <span>{titleCase(p.name)}</span>
                  {p.latestDate ? <small className="ac-pub-date">Latest: {day(p.latestDate).replace(/ \d{4}$/, "")}</small> : null}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {pub ? (
        <>
          <div className="ac-step"><span className="ac-step-n">2</span><strong>Choose the plan</strong></div>
          <div className="ac-plans" role="radiogroup" aria-label="Plan">
            {plans.map((x) => (
              <button type="button" key={x.key} role="radio" aria-checked={plan === x.key} className={`ac-plan${plan === x.key ? " on" : ""}`} onClick={() => setPlan(x.key)}>
                <span>{x.label}</span><strong>{naira(x.price)}</strong>
              </button>
            ))}
          </div>

          <div className="ac-step"><span className="ac-step-n">3</span><strong>Who is it for?</strong></div>
          <div className="ac-grid2">
            <label className="ac-field"><span>First name</span><input className="ac-input" value={to.firstName} onChange={(e) => setTo({ ...to, firstName: e.target.value })} /></label>
            <label className="ac-field"><span>Last name</span><input className="ac-input" value={to.lastName} onChange={(e) => setTo({ ...to, lastName: e.target.value })} /></label>
            <label className="ac-field"><span>Their email</span><input className="ac-input" type="email" value={to.email} onChange={(e) => setTo({ ...to, email: e.target.value })} inputMode="email" autoComplete="off" />
              {typo ? <small>Did you mean <button type="button" className="ac-link" onClick={() => setTo({ ...to, email: `${to.email.split("@")[0]}@${typo}` })}>{to.email.split("@")[0]}@{typo}</button>?</small> : <small>They’ll read on their own TNS account with this email.</small>}
            </label>
          </div>

          <div className="ac-review">
            <span><strong>{titleCase(pub.name)} · {chosen ? chosen.label : "—"}</strong><small>{to.firstName || to.lastName ? `For ${to.firstName} ${to.lastName}`.trim() : "For…"}{to.email ? ` (${to.email})` : ""}</small></span>
            <button type="button" className="ac-btn" onClick={pay} disabled={!chosen}>{chosen ? `Pay ${naira(chosen.price)}` : "Choose a plan"}</button>
          </div>
          <p className="ac-muted">You’ll pay securely with Paystack (card, bank transfer or USSD).</p>
        </>
      ) : null}
      {err ? <p className="ac-note err" role="alert">{err}</p> : null}
    </section>
  );
}

export function Gifts() {
  const { subAccountSuccess, SetsubAccountSuccess, setgetPublicationDetails } = useContext(PaymentContext);
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => httpGet("bought_by_me").then((r) => {
    if (!r || r.er) {
      const msg = errText(r, "We couldn’t load your gifts.");
      if (/no sub ?account/i.test(msg)) { setErr(""); setList([]); return; }
      setErr(msg); setList([]); return;
    }
    setErr("");
    setList((r.data && (r.data.subscriptions || r.data.items)) || []);
  });
  useEffect(() => {
    if (SetsubAccountSuccess) SetsubAccountSuccess(false);
    load();
    return () => { if (setgetPublicationDetails) setgetPublicationDetails(null); };
  }, []);
  useEffect(() => {
    if (subAccountSuccess === true) {
      setNote({ ok: true, text: "Gift paid. They’ll get an email with how to start reading." });
      load();
      if (SetsubAccountSuccess) SetsubAccountSuccess(false);
    }
  }, [subAccountSuccess]);
  const stop = async (s) => {
    if (!window.confirm(`Stop renewing ${who(s).name || "this"} ${paper(s)} subscription? They keep reading until ${day(s.nextPaymentDate) || "the end of the period"}.`)) return;
    setBusy(s.id);
    const r = await httpPost(`cancel_buy_for_me_subscription/${s.id}`);
    setBusy("");
    if (!r || r.er) return setNote({ ok: false, text: errText(r, "We couldn’t stop that renewal. Please try again.") });
    setNote({ ok: true, text: (r && r.message) || "Renewal stopped." });
    load();
  };
  const now = Date.now();
  return (
    <>
      <div className="ac-title"><h1>Gifts for others</h1><p>Buy a subscription for family, friends, colleagues or staff. They read on their own account, and you can stop renewing any time.</p></div>
      {open ? (
        <GiftForm onClose={() => setOpen(false)} onStarted={(what) => { setOpen(false); setNote({ ok: true, text: `Payment started for ${what}. Once it’s confirmed, they’ll appear below.` }); }} />
      ) : (
        <section className="ac-card ac-gift-cta">
          <span className="ac-offer-icon" aria-hidden="true">🎁</span>
          <span className="ac-offer-text"><strong>Give a paper</strong><small>Choose the paper and plan, enter their name and email, and pay. We’ll email them how to start reading.</small></span>
          <button type="button" className="ac-btn" onClick={() => { setNote(null); setOpen(true); }}>Give a gift</button>
        </section>
      )}
      <Note note={note} />
      <h2 className="ac-section-title">People you’ve gifted</h2>
      {list === null ? <p className="ac-muted">Loading…</p> : err ? <div className="ac-card"><p className="ac-muted">{err}</p></div> : !list.length ? (
        <div className="ac-card ac-empty"><p>You haven’t given any gifts yet. A subscription makes a thoughtful present, and for teams it keeps everyone informed.</p></div>
      ) : list.map((s) => {
        const w = who(s);
        const next = s.nextPaymentDate ? new Date(s.nextPaymentDate).getTime() : null;
        const active = String(s.status || "active").toLowerCase() === "active" && (!next || next > now);
        const status = !active ? ["Ended", ""] : s.isCancelled ? ["Renewal off", "warn"] : next && next - now < 7 * 864e5 ? ["Renews soon", "info"] : ["Active", "ok"];
        return (
          <div className="ac-card ac-sub" key={s.id}>
            <span className="ac-avatar">{(w.name || "?")[0]}</span>
            <span className="ac-sub-text">
              <span className="ac-sub-name"><strong>{w.name || w.email || "Recipient"}</strong><span className={`ac-pill ${status[1]}`}>{status[0]}</span></span>
              <small>{w.email}</small>
              <span className="ac-sub-line">{paper(s)} · {PLAN[s.subType] || titleCase(s.subType || "")}{Number(s.amount) ? ` · ${naira(s.amount)}` : ""}{next ? ` · ${active ? (s.isCancelled ? "ends" : "renews") : "ended"} ${day(s.nextPaymentDate)}` : ""}</span>
            </span>
            <span className="ac-sub-actions">
              {active && !s.isCancelled ? <button type="button" className="ac-btn2" onClick={() => stop(s)} disabled={busy === s.id}>{busy === s.id ? "Stopping…" : "Stop renewal"}</button> : null}
            </span>
          </div>
        );
      })}
    </>
  );
}

function who(s) {
  const u = s.user || s.User || s.subUser || s.recipient || s.owner || {};
  const first = s.firstName || u.firstName || "";
  const last = s.lastName || u.lastName || "";
  return { name: `${first} ${last}`.trim(), email: s.email || u.email || "" };
}
function paper(s) {
  const pb = s.publication || s.Publication || {};
  return titleCase(pb.name || s.publicationName || "Paper");
}
